import { useRef } from 'react';
import { toPostUI } from '../../mappers/posts/toPostUI';
import { Box } from '../../ui/layout/Box';
import { ResizableGrid } from '../../ui/layout/ResizableGrid/ResizableGrid';
import { PostCardGrid } from '../../ui/posts/PostCardGrid/PostCardGrid';
import { calculateGridCardHeight } from '../../ui/posts/PostCardGrid/utils';
import { EventSource } from '../../utils/analyticsService';
import { UsePostsCollectionPayload } from '../post/usePostCollection';
import { withPostCallbacks } from '../post/withPostCallbacks';
import { SearchNotFound } from './SearchNotFound';
import { useResetScroll } from './hooks/useResetScroll';

interface SearchPostProps {
    postCollection: UsePostsCollectionPayload;
}

const EnchancedPostGridCardCompact = withPostCallbacks(PostCardGrid, EventSource.SEARCH);

export const SearchPost = (props: SearchPostProps) => {
    const { postCollection } = props;
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const hasPosts = postCollection.isLoading || postCollection.items.length > 0;

    const { onScroll } = useResetScroll(gridContainerRef, 'searchPostsScrollTop', hasPosts);

    const getItemHeight = (index: number, width: number) => {
        const postModel = postCollection.items[index];
        if (!postModel) {
            return 0;
        }

        return calculateGridCardHeight(width, toPostUI(postModel).content, postModel.postType);
    };

    const renderItem = (index: number, columns: number, isVisible?: boolean) => {
        const postModel = postCollection.items[index];

        if (!postModel) {
            return null;
        }

        return (
            <EnchancedPostGridCardCompact
                post={postModel}
                index={index}
                gridColumns={columns}
                isVisible={isVisible}
            />
        );
    };

    return hasPosts ? (
        <Box
            height="100%"
            overflowY="auto"
            ref={gridContainerRef}
            onScroll={onScroll}
            scrollbarGutter="stable"
        >
            <ResizableGrid
                key={`${postCollection.items[0]?.id}-search-post-grid`}
                marginTop={2}
                bottomYMargin={8}
                containerRef={gridContainerRef}
                items={postCollection.items}
                loadMore={postCollection.onLoadNext}
                getItemHeight={getItemHeight}
                renderItem={renderItem}
            />
        </Box>
    ) : (
        <SearchNotFound />
    );
};
